import BaseComponent from '../base.js';
import tag from '../utilComponents.js';
import Switcher from '../switcher/switcher.js';

import StartBtn from '../controls/start/startButton.js';
import NewGameBtn from '../controls/newGame/newGameButton.js';
import RepeatBtn from '../controls/repeat/repeatButton.js';
import NextBtn from '../controls/next/nextButton.js';

import * as styles from './dashboard.module.scss';

import config from '../../gameConfig.js';

const { ROUNDS_NUMBER, DIFFICULTY_ARRAY } = config;

export default class Dashboard extends BaseComponent {
  // #region child components
  /**
   * @type {BaseComponent}
   * @private
   */
  inputBlock;

  /**
   * @type {BaseComponent}
   * @private
   */
  messageBlock;

  /**
   * @type {BaseComponent}
   * @private
   */
  roundBlock;
  // #endregion

  constructor() {
    super({ classes: [styles.dashboard] });

    this.createComponents();
    this.setListeners();
  }

  setListeners() {
    window.addEventListener('changestart', this.handleChangeStart.bind(this));
    window.addEventListener('changeinput', this.handleChangeInput.bind(this));
    window.addEventListener('changeround', this.handleChangeRound.bind(this));
    window.addEventListener(
      'changefeedback',
      this.handleChangeFeedback.bind(this)
    );
  }

  /**
   * Create all necessary dashboard components.
   * @returns {void}
   * @private
   */
  createComponents() {
    const controls = this.createControls();
    const outputPanel = this.createOutputPanel();

    this.appendChildren(controls, outputPanel);
  }

  /**
   * Creates control components.
   * @returns {BaseComponent}
   * @private
   */
  createControls() {
    const switcher = this.createSwitchers();

    return tag.div(
      { classes: [styles.controls] },
      tag.div(
        { classes: [styles.container] },
        new StartBtn(),
        new NewGameBtn()
      ),
      tag.div({ classes: [styles.container] }, switcher),
      tag.div({ classes: [styles.container] }, new RepeatBtn(), new NextBtn())
    );
  }

  /**
   * Creates output components.
   * @returns {BaseComponent}
   * @private
   */
  createOutputPanel() {
    this.roundBlock = tag.div(
      { classes: [styles.textBlock] },
      tag.span({
        classes: [styles.text],
        text: `Round ${this.state.round} of ${ROUNDS_NUMBER}`,
      })
    );
    this.inputBlock = tag.div(
      { classes: [styles.textBlock] },
      tag.span({ classes: [styles.text], text: 'Input: ' })
    );
    this.messageBlock = tag.div({
      classes: [styles.textBlock, styles.textBlockFeedback],
    });

    return tag.div(
      { classes: [styles.output] },
      tag.div(
        { classes: [styles.container, styles.containerText] },
        this.roundBlock
      ),
      tag.div(
        { classes: [styles.container, styles.containerText] },
        this.inputBlock
      ),
      tag.div(
        { classes: [styles.container, styles.containerText] },
        this.messageBlock
      )
    );
  }

  /**
   * Creates difficulty switcher.
   * @returns {BaseComponent}
   * @private
   */
  createSwitchers() {
    const switchers = tag.div({ classes: [styles.switchers] });

    DIFFICULTY_ARRAY.forEach((level) => {
      const switcher = new Switcher(level);
      switchers.appendSingle(switcher);
    });

    const switchersBlock = tag.div(
      { classes: [styles.switchersBlock] },
      tag.div({
        classes: [styles.switchersBlockHeading],
        text: 'difficulty level:',
      }),
      switchers
    );

    return switchersBlock;
  }

  /**
   * Handles the state's start property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeStart(e) {
    const { start } = e.detail;

    if (start) {
      this.roundBlock.addClasses(styles.textBlockActive);
      this.inputBlock.addClasses(styles.textBlockActive);
    } else {
      this.roundBlock.removeClasses(styles.textBlockActive);
      this.inputBlock.removeClasses(styles.textBlockActive);
    }
  }

  /**
   * Handles the state's input property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeInput(e) {
    const { input } = e.detail;

    const text = `Input: ${input.join(' ')}`;
    this.inputBlock.setText(text);
  }

  /**
   * Handles the state's feedback property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeFeedback(e) {
    const { feedback } = e.detail;
    const { input, sequence, sequenceIndex } = this.state;
    const isRoundComplete =
      input.length > 0 && sequenceIndex === sequence.length;

    if (feedback) {
      this.messageBlock.setText(feedback);
      this.messageBlock.addClasses(styles.textBlockActive);

      if (!isRoundComplete) {
        this.messageBlock.addClasses(styles.textBlockFeedbackDanger);
      } else {
        this.messageBlock.removeClasses(styles.textBlockFeedbackDanger);
      }
    } else {
      this.messageBlock.removeClasses(
        styles.textBlockActive,
        styles.textBlockFeedbackDanger
      );
    }
  }

  /**
   * Handles the state's round property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeRound(e) {
    const { round } = e.detail;

    const text = `Round ${round} of ${ROUNDS_NUMBER}`;
    this.roundBlock.setText(text);
  }
}
